import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { FaArrowLeft, FaSave } from 'react-icons/fa';
import { getAreaForEdit, updateArea } from '../../../models/areasModel';

function AreaEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    id: '',
    area_name: '',
    sub_areas: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAreaData();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAreaData = async () => {
    try {
      setLoading(true);
      const response = await getAreaForEdit(id);
      
      if (response.status === 1) {
        const areaData = response.data.row;
        setFormData({
          id: areaData.area_id,
          area_name: areaData.area_name || '',
          sub_areas: response.data.sub_areas || ''
        });
      } else {
        alert('Failed to load area data');
        navigate('/dashboard/areas');
      }
    } catch (error) {
      console.error('Error fetching area data:', error);
      alert('Error loading area data');
      navigate('/dashboard/areas');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.area_name.trim()) {
      alert('Area name is required');
      return;
    }

    try {
      setSaving(true);
      const response = await updateArea(formData);
      
      if (response.status === 1) {
        alert('Area updated successfully!');
        navigate('/dashboard/areas');
      } else {
        alert(response.message || 'Failed to update area');
      }
    } catch (error) {
      console.error('Error updating area:', error);
      alert('Error updating area');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center">
        <Link
          to="/dashboard/areas"
          className="mr-4 text-gray-600 hover:text-gray-800"
        >
          <FaArrowLeft size={20} />
        </Link>
        <h1 className="text-2xl font-semibold text-gray-800">Edit Area</h1>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="area_name" className="block text-sm font-medium text-gray-700 mb-2">
              Area Name *
            </label>
            <input
              type="text"
              id="area_name"
              name="area_name"
              value={formData.area_name}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter area name"
              required
            />
          </div>

          <div>
            <label htmlFor="sub_areas" className="block text-sm font-medium text-gray-700 mb-2">
              Sub-Areas
            </label>
            <textarea
              id="sub_areas"
              name="sub_areas"
              value={formData.sub_areas}
              onChange={handleChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter sub-areas separated by commas (e.g., Sub Area 1, Sub Area 2, Sub Area 3)"
            />
            <p className="mt-1 text-sm text-gray-500">
              Enter multiple sub-areas separated by commas. Leave empty if no sub-areas needed.
            </p>
          </div>

          <div className="flex justify-end space-x-4">
            <Link
              to="/dashboard/areas"
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving}
              className={`px-4 py-2 rounded-md text-white flex items-center ${
                saving
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-500 hover:bg-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
              }`}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Updating...
                </>
              ) : (
                <>
                  <FaSave className="mr-2" />
                  Update Area
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AreaEdit;
